// ./src/utils/onboarding.ts
import { UserProfile } from "@/utils/api";

/**
 * Check if a user needs to complete onboarding.
 * A user needs onboarding if:
 * - Their profile doesn't exist yet (null), OR
 * - Their profile exists but hasn't been configured (homeCurrency not set)
 *
 * @param userProfile - The user's profile data
 * @returns true if the user needs to complete onboarding
 */
export const needsOnboarding = (
  userProfile: UserProfile | null | undefined
): boolean => {
  // If no profile exists, user definitely needs onboarding
  if (!userProfile) {
    return true;
  }

  // Check if essential preferences have been set
  // homeCurrency is the primary indicator - it's required for proper expense tracking
  return !userProfile.homeCurrency;
};

/**
 * Check if onboarding status can be determined.
 * Returns true once profile loading is complete.
 * Note: userProfile being null is a valid state (means no profile exists = needs onboarding)
 *
 * @param isLoaded - Whether the profile has finished loading
 * @returns true if we can determine onboarding status
 */
export const canDetermineOnboardingStatus = (isLoaded: boolean): boolean => {
  return isLoaded;
};

/**
 * Routes that should bypass onboarding check.
 * These are pages that should be accessible even if onboarding is not complete.
 */
export const ONBOARDING_BYPASS_ROUTES = [
  "/onboarding",
  "/account/signin",
  "/account/signup",
  "/account/forgot-password",
  "/account/reset-password",
  "/account/accept-invitation",
  "/server-error",
];

/**
 * Check if a given path should bypass onboarding redirect.
 *
 * @param pathname - The current route pathname
 * @returns true if this route should not trigger onboarding redirect
 */
export const shouldBypassOnboarding = (pathname: string): boolean => {
  return ONBOARDING_BYPASS_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  );
};
